/**
 * B2BOrdersListView — Vue Liste + Mini-dashboard KPI du module SADA B2B.
 *
 * Extrait de l'ex-`B2BTab.jsx` (Phase A++) sans changement de comportement.
 *   - `OrdersList` : tableau filtrable des dossiers (status/payment/channel)
 *     synchronisé avec un `quickStatus` propagé par les tuiles KPI.
 *   - `MiniDashboard` : 10 tuiles dont 3 issues de la Phase A++
 *     (révisions à traiter, à relancer, refusées définitif).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Filter, Plus, Loader2, ShoppingBag, Clock, Package, AlertCircle, Wallet,
  CheckCircle2, ChevronRight, MessageSquareWarning, Bell, Ban,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { GMPCatalogButton } from './GMPCatalog';
import {
  API, auth, CHANNEL_ICONS, CHANNEL_LABELS, StatusBadge, PaymentBadge,
  fmtDateTime,
} from './B2BConstants';

export const OrdersList = ({ meta, canWrite, onOpen, onCreate, refreshKey, quickStatus, onCatalogChanged }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', status_payment: '', channel: '' });

  // Phase A++ : lorsque l'utilisateur clique un KPI, on synchronise le filtre statut.
  useEffect(() => {
    if (quickStatus !== undefined && quickStatus !== null) {
      setFilters((f) => ({ ...f, status: quickStatus || '' }));
    }
  }, [quickStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.status_payment) params.set('status_payment', filters.status_payment);
      if (filters.channel) params.set('channel', filters.channel);
      const r = await axios.get(`${API}/b2b/orders?${params.toString()}`, { headers: auth() });
      setOrders(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const empty = !loading && orders.length === 0;

  return (
    <div className="space-y-3" data-testid="b2b-orders-list">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-zinc-400" />
          <select className="h-9 px-2 rounded-md border border-zinc-300 text-sm bg-white" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="b2b-filter-status">
            <option value="">Tous les statuts</option>
            {meta?.statuses?.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select className="h-9 px-2 rounded-md border border-zinc-300 text-sm bg-white" value={filters.status_payment} onChange={(e) => setFilters((f) => ({ ...f, status_payment: e.target.value }))} data-testid="b2b-filter-payment">
            <option value="">Tous les paiements</option>
            {meta?.payment_statuses?.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <select className="h-9 px-2 rounded-md border border-zinc-300 text-sm bg-white" value={filters.channel} onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value }))} data-testid="b2b-filter-channel">
            <option value="">Tous les canaux</option>
            {meta?.channels?.map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c] || c}</option>)}
          </select>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <GMPCatalogButton onChange={() => onCatalogChanged?.()} />
            <Button onClick={onCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="b2b-create-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouveau dossier
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-6 text-center text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : empty ? (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="p-8 text-center">
            <ShoppingBag className="w-10 h-10 mx-auto text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">Aucun dossier B2B pour ces filtres.</p>
            {canWrite && (
              <Button variant="outline" className="mt-3" onClick={onCreate}>
                <Plus className="w-4 h-4 mr-1" /> Créer le premier dossier
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="text-xs text-zinc-500 uppercase bg-zinc-50">
              <tr>
                <th className="text-left p-2">Clinique</th>
                <th className="text-left p-2">Canal</th>
                <th className="text-left p-2">Statut</th>
                <th className="text-left p-2">Paiement</th>
                <th className="text-right p-2">Lignes</th>
                <th className="text-left p-2">Créé</th>
                <th className="text-left p-2">Modifié</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const ChIcon = CHANNEL_ICONS[o.channel] || AlertCircle;
                const sLabel = meta?.statuses?.find((s) => s.id === o.status)?.label || o.status;
                const pLabel = meta?.payment_statuses?.find((s) => s.id === o.status_payment)?.label || o.status_payment;
                return (
                  <tr key={o.id} className="border-t border-zinc-100 hover:bg-amber-50/30 cursor-pointer" onClick={() => onOpen(o.id)} data-testid={`b2b-row-${o.id}`}>
                    <td className="p-2 font-medium text-zinc-900">
                      <div className="flex flex-col">
                        <span>{o.clinic_name}</span>
                        {o.requester_name && <span className="text-xs text-zinc-500">{o.requester_name}</span>}
                      </div>
                    </td>
                    <td className="p-2 text-zinc-600">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <ChIcon className="w-3.5 h-3.5" /> {CHANNEL_LABELS[o.channel] || o.channel}
                      </span>
                    </td>
                    <td className="p-2"><StatusBadge id={o.status} label={sLabel} /></td>
                    <td className="p-2"><PaymentBadge id={o.status_payment} label={pLabel} /></td>
                    <td className="p-2 text-right font-mono">{o.items?.length || 0}</td>
                    <td className="p-2 text-zinc-500 text-xs">{fmtDateTime(o.created_at)}</td>
                    <td className="p-2 text-zinc-500 text-xs">{fmtDateTime(o.updated_at)}</td>
                    <td className="p-2"><ChevronRight className="w-4 h-4 text-zinc-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const MiniDashboard = ({ refreshKey, onFilterStatus }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/b2b/dashboard`, { headers: auth() })
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [refreshKey]);

  if (!data) return null;
  const items = [
    { id: 'demande_recue', label: 'Demandes reçues', icon: ShoppingBag, count: data.by_status?.demande_recue || 0, color: 'text-blue-600' },
    { id: 'en_cours_cotation', label: 'En cotation', icon: Clock, count: data.by_status?.en_cours_cotation || 0, color: 'text-indigo-600' },
    { id: 'cotation_envoyee', label: 'Cotation envoyée', icon: Clock, count: data.by_status?.cotation_envoyee || 0, color: 'text-amber-600' },
    { id: 'modification_requested', label: 'Révisions à traiter', icon: MessageSquareWarning, count: data.by_status?.modification_requested || 0, color: 'text-amber-700' },
    { id: 'a_relancer', label: 'À relancer', icon: Bell, count: data.by_status?.a_relancer || 0, color: 'text-fuchsia-600' },
    { id: 'cotation_refused_final', label: 'Refusées (définitif)', icon: Ban, count: data.by_status?.cotation_refused_final || 0, color: 'text-red-600' },
    { id: 'en_preparation', label: 'En préparation', icon: Package, count: data.by_status?.en_preparation || 0, color: 'text-cyan-600' },
    { id: 'overdue', label: 'En retard >48h', icon: AlertCircle, count: data.overdue_count || 0, color: 'text-red-600' },
    { id: 'payment', label: 'Paiements à relancer', icon: Wallet, count: data.payment_to_chase || 0, color: 'text-orange-600' },
    { id: 'closed_month', label: 'Clôturés (mois)', icon: CheckCircle2, count: data.closed_this_month || 0, color: 'text-emerald-600' },
  ];
  const nonFilterables = new Set(['overdue', 'payment', 'closed_month']);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-4" data-testid="b2b-mini-dashboard">
      {items.map((it) => {
        const Icon = it.icon;
        const isFilter = !nonFilterables.has(it.id);
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => isFilter && onFilterStatus?.(it.id)}
            className={`text-left p-3 bg-white rounded-lg border border-zinc-200 transition-colors ${isFilter ? 'hover:border-amber-400 cursor-pointer' : 'cursor-default'}`}
            data-testid={`b2b-kpi-${it.id}`}
          >
            <Icon className={`w-5 h-5 ${it.color}`} />
            <p className="text-2xl font-bold text-zinc-900 mt-1">{it.count}</p>
            <p className="text-[11px] text-zinc-500 leading-tight">{it.label}</p>
          </button>
        );
      })}
    </div>
  );
};
